import { useEffect, useState } from "react";
import { X, Save } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../services/api.js";

export default function EditInvoiceModal({
  open,
  onClose,
  invoiceId,
  token,
  selectedInvoice,
  onSuccess,
}) {
  const [items, setItems] = useState([]);
  const [taxrate, setTaxrate] = useState(0);
  const [inputTax, setInputTax] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  // ===============================
  // LOAD DATA (ROW FIRST, FETCH FALLBACK)
  // ===============================
  useEffect(() => {
    if (!open || !invoiceId) return;

    const load = async () => {
      try {
        setLoadingData(true);

        // 1️⃣ Ambil data dari row (langsung tampil)
        if (selectedInvoice) {
          setTaxrate(selectedInvoice.taxrate || 0);
          setNotes(selectedInvoice.notes || "");
          setInputTax(Number(selectedInvoice.tax) > 0);
        }

        // 2️⃣ Fetch items (WAJIB)
        const res = await api.get(`/invoice-detail/${invoiceId}`, {
          headers: { Authorization: token },
        });

        const inv = res.data.data;

        if (!Array.isArray(inv.items)) {
          throw new Error("Invoice items invalid");
        }

        setItems(
          inv.items.map((i) => ({
            id: i.id,
            description: i.description,
            amount: Number(i.amount),
          }))
        );
      } catch (err) {
        console.error("LOAD INVOICE ERROR:", err.response || err);
        Swal.fire(
          "Error",
          err.response?.data?.message || "Gagal memuat data invoice",
          "error"
        );
        onClose();
      } finally {
        setLoadingData(false);
      }
    };

    load();
  }, [open, invoiceId, selectedInvoice, token, onClose]);

  if (!open) return null;

  // ===============================
  // CALC SUMMARY
  // ===============================
  const subtotal = items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const taxNominal = inputTax ? (subtotal * taxrate) / 100 : 0;
  const total = subtotal + taxNominal;

  const updateItem = (idx, field, val) => {
    const copy = [...items];
    copy[idx][field] = field === "amount" ? Number(val) : val;
    setItems(copy);
  };

  // ===============================
  // SUBMIT
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      await api.put(
        `/invoice/${invoiceId}`,
        {
          taxrate,
          input_tax: inputTax,
          notes,
          items,
        },
        {
          headers: { Authorization: token },
        }
      );

      Swal.fire("Berhasil", "Invoice berhasil diperbarui", "success");
      onClose();
      onSuccess?.();
    } catch (err) {
      Swal.fire("Error", err || "Gagal update invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800">
        {/* ================= HEADER ================= */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 p-4 border-b dark:border-slate-800 flex justify-between items-center z-20">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              Edit Invoice
            </h2>
            <p className="text-xs text-slate-500">
              Perbarui item dan perhitungan invoice
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"
          >
            <X className="w-5 h-5 dark:text-slate-400" />
          </button>
        </div>

        {loadingData ? (
          <div className="p-6 text-sm text-slate-500">Memuat data invoice…</div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* ================= ITEMS ================= */}
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <div className="w-1.5 h-5 bg-blue-600 rounded-full"></div>
                Daftar Item
              </h3>

              <div className="space-y-3">
                {items.map((item, idx) => (
                  <div
                    key={item.id}
                    className="flex flex-wrap md:flex-nowrap gap-3 p-4 bg-white dark:bg-slate-800/40 border dark:border-slate-800 rounded-xl"
                  >
                    <div className="flex-1">
                      <input
                        type="text"
                        className="w-full bg-transparent border-b dark:border-slate-700 dark:text-white outline-none focus:border-blue-500"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(idx, "description", e.target.value)
                        }
                      />
                    </div>
                    <div className="w-full md:w-40 relative">
                      <span className="absolute left-0 top-2 text-slate-400 text-sm">
                        Rp
                      </span>
                      <input
                        type="number"
                        className="w-full pl-7 bg-transparent border-b dark:border-slate-700 dark:text-white outline-none focus:border-blue-500 text-right"
                        value={item.amount}
                        onChange={(e) =>
                          updateItem(idx, "amount", e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ================= SUMMARY ================= */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6 border-t dark:border-slate-800">
              {/* NOTES */}
              <div>
                <label className="block text-sm font-semibold mb-2 dark:text-slate-300">
                  Catatan Internal
                </label>
                <textarea
                  rows={4}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full p-3 bg-slate-50 dark:bg-slate-800 border dark:border-slate-700 rounded-xl dark:text-white outline-none"
                />
              </div>

              {/* TOTAL */}
              <div className="space-y-3 bg-blue-50/50 dark:bg-blue-900/10 p-5 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                <div className="flex justify-between text-slate-600 dark:text-slate-400">
                  <span>Subtotal</span>
                  <span className="font-mono">
                    Rp {subtotal.toLocaleString()}
                  </span>
                </div>

                <div>
                  <label className="text-[10px] uppercase text-slate-500 font-bold flex justify-between">
                    Tax (%) <span>Nominal</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      className="flex-1 bg-transparent border-b dark:border-slate-700 dark:text-white outline-none focus:border-blue-500"
                      value={taxrate}
                      onChange={(e) => setTaxrate(Number(e.target.value))}
                      disabled={!inputTax}
                    />
                    <label className="flex items-center gap-2 text-sm dark:text-slate-300">
                      <input
                        type="checkbox"
                        checked={inputTax}
                        onChange={(e) => setInputTax(e.target.checked)}
                      />
                      Apply
                    </label>
                  </div>
                  {inputTax && (
                    <p className="text-[10px] text-blue-500 mt-1 italic">
                      + Rp {taxNominal.toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="pt-4 flex justify-between items-center">
                  <span className="font-bold text-slate-800 dark:text-white">
                    Total
                  </span>
                  <span className="text-2xl font-black text-blue-600 dark:text-blue-400 font-mono">
                    Rp {total.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* ================= FOOTER ================= */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium"
              >
                Batal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-blue-500/30"
              >
                <Save className="w-5 h-5" />
                Simpan Perubahan
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
